import { ECurrency, EProductAdditionalUnitPriceType } from '@ringana/configs';
import config from '../../config';
import {
  INewSingleProductData,
  IProduct,
  IProductDetails,
  IProductPrice,
  IProductPrices,
  IUnitPriceData,
} from '@/common/interfaces/products.interface';
import OldFormatHelper from '@/helpers/OldFormatHelper';
import CustomerHelper from '@/helpers/CustomerHelper';
import { ICalendarDoorProduct } from '@/common/interfaces/announcement-bar.interface';

export default class ProductHelper {
  public static discountOrSoldOutImage(isPartner: boolean, product: IProduct): string {
    if(product.soldOut) {
      return config.products.images.soldOut;
    }
    const percentage = isPartner ? product.prices.partner.discountPercentage : product.prices.customer.discountPercentage;
    const { availableDiscounts } = config.products;

    if(percentage && availableDiscounts.indexOf(percentage) >= 0) {
      // since percentage is included in availableDiscounts it is a valid index of config.products.images
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return config.products.images[percentage];
    }
    return ``;
  }

  public static getPriceUnformatted(isPartner: boolean, prices: IProductPrices): number | string {
    if(!prices || !prices.customer || !prices.customer.brutto) {
      return ``;
    }
    return isPartner ? prices.partner.brutto : prices.customer.brutto;
  }

  public static getMaxAmountForProduct(item: IProduct): number {
    if(item.singleShipmentOnly) {
      return 1;
    }
    if(!item.maxPerCart) {
      return 99;
    }
    return item.maxPerCart;
  }

  public static getUnitPriceTypeLocizeKey(type: number | null): string {
    switch(type) {
      case EProductAdditionalUnitPriceType.CAPS:
        return `products-ufe:unitPriceType.cap`;
      case EProductAdditionalUnitPriceType.SACHET:
        return `products-ufe:unitPriceType.sachet`;
      default:
        return ``;
    }
  }

  public static formatAdditionalUnitPrice(product: IProduct, unitPriceData: IUnitPriceData, isPartner: boolean): { text: string; unitPriceType: string } {
    let basePrice: number;
    if(isPartner) {
      basePrice = product.prices.partner.bruttoWithoutDiscount ?? product.prices.partner.brutto;
    } else {
      basePrice = product.prices.customer.bruttoWithoutDiscount ?? product.prices.customer.brutto;
    }
    const pricePerUnit = unitPriceData.amount ? basePrice / unitPriceData.amount : 0;

    return {
      text: `${unitPriceData.amount} {{unitPriceType}} (${OldFormatHelper.getPrice(pricePerUnit, product.currency)} / 1 {{unitPriceType}})`,
      unitPriceType: this.getUnitPriceTypeLocizeKey(unitPriceData.type),
    };
  }

  public static getMyPrice(productInfo: IProduct | ICalendarDoorProduct, isPartner = false, showCurrency = true): string {
    if(!productInfo.prices.customer.brutto) {
      return ``;
    }

    let currency = ECurrency.EUR;
    let price = isPartner
      ? productInfo.prices.partner.brutto
      : productInfo.prices.customer.brutto;

    if(!price) {
      price = 0;
    }

    if(`currency` in productInfo) {
      currency = (productInfo as IProduct).currency;
    }

    return showCurrency ? OldFormatHelper.getPrice(price, currency) : price.toFixed(2).replace(`.`, `,`);
  }

  public static getMyOriginalPrice(productInfo: IProduct | ICalendarDoorProduct, isPartner = false, showCurrency = true): string {
    if(!productInfo.prices.customer.bruttoWithoutDiscount) {
      return ``;
    }

    let currency = ECurrency.EUR;
    let price = isPartner
      ? productInfo.prices.partner.bruttoWithoutDiscount
      : productInfo.prices.customer.bruttoWithoutDiscount;

    if(!price) {
      price = 0;
    }

    if(`currency` in productInfo) {
      currency = (productInfo as IProduct).currency;
    }

    return showCurrency ? OldFormatHelper.getPrice(price, currency) : price.toFixed(2).replace(`.`, `,`);
  }

  public static showDeliveryStatus(deliveryStatusId?: number): boolean {
    const deliveryStatusIdsToDisplay = [
      12265,
      136153,
      12268,
      38910,
      45571,
      59116,
      103177,
      130291,
      304743,
      250608,
      271733,
      294714,
      435062,
    ];

    return deliveryStatusIdsToDisplay.includes(deliveryStatusId ?? 0);
  }

  public static transformZapiProductAndPapiProductToProductDetails(zapiProductData: IProduct, singleProductData: INewSingleProductData): IProductDetails {
    const price: IProductPrice = CustomerHelper.getPartnerStatus() ? zapiProductData.prices.partner : zapiProductData.prices.customer;
    return {
      amount: parseFloat(zapiProductData.contentsAmount),
      basePrice: zapiProductData.basePrice,
      categories: singleProductData.categories,
      description: singleProductData.usp,
      discountPrice: price.bruttoWithoutDiscount ? price.brutto : -1,
      images: [],
      informationLinkColor: singleProductData.informationLinkColor,
      matchcode: zapiProductData.matchcode,
      price: price.bruttoWithoutDiscount ? price.bruttoWithoutDiscount : price.brutto,
      slug: zapiProductData.link.split(`/`)[2],
      title: singleProductData.title,
      unit: zapiProductData.unitContents,
    };
  }

  public static transformProductInfosToLocizeKeys(product: IProduct): IProduct {
    let deliveryStatus = ``;
    switch(product.deliveryStatusId) {
      case 12265:
        deliveryStatus = `products-overview:soldOut`;
        break;
      case 136153:
        deliveryStatus = `products-overview:noLongerAvailable`;
        break;
      case 12268:
        deliveryStatus = `products-overview:temporarilyUnavailable`;
        break;
      case 38910:
        deliveryStatus = `products-overview:presale`;
        break;
      case 45571:
        deliveryStatus = `products-overview:automaticReDelivery`;
        break;
      case 59116:
        deliveryStatus = `products-overview:reDelivery`;
        break;
      case 103177:
        deliveryStatus = `products-overview:comingSoon`;
        break;
      case 130291:
        deliveryStatus = `products-overview:reDelivery`;
        break;
      case 304743:
        deliveryStatus = `products-overview:reDelivery`;
        break;
      case 250608:
        deliveryStatus = `products-overview:notForSale`;
        break;
      case 271733:
        deliveryStatus = `products-overview:reDelivery`;
        break;
      case 294714:
        deliveryStatus = `products-overview:notSoldSeparately`;
        break;
      case 435062:
        deliveryStatus = `products-overview:reDelivery`;
        break;
      default:
        deliveryStatus = `products-overview:defaultDeliveryState`;
    }

    return {
      ...product,
      availableVariants: product.availableVariants.map(variant => ({
        ...variant,
        unit: `products-ufe:productInfos.units.unit${variant.unitId}`,
      })),
      basePrice: {
        ...product.basePrice,
        unit: `products-ufe:productInfos.units.unit${product.basePrice.unitId}`,
      },
      deliveryStatus,
      unit: `products-ufe:productInfos.units.unit${product.unitId}`,
      unitContents: `products-ufe:productInfos.units.unit${product.unitContentsId}`,
    };
  }
}
